import React from 'react';
import { Keyboard, Search, Navigation, HelpCircle } from 'lucide-react';
import { useEnhancedKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { Modal, Button } from '../ui';

interface KeyboardShortcutsHelpProps {
  /** Whether the help modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
}

interface ShortcutItemProps {
  shortcut: string;
  description: string;
  type?: 'primary' | 'secondary' | 'vim';
}

const ShortcutItem: React.FC<ShortcutItemProps> = ({ shortcut, description, type = 'primary' }) => {
  const typeStyles = {
    primary: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
    secondary: 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]',
    vim: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
  };

  return (
    <div className="flex items-center justify-between py-2 px-3 hover:bg-[var(--color-bg-subtle)] rounded-md">
      <span className="text-sm text-[var(--color-text-primary)] flex-1">
        {description}
      </span>
      <kbd className={`px-2 py-1 text-xs font-mono rounded ${typeStyles[type]} ml-3 flex-shrink-0`}>
        {shortcut}
      </kbd>
    </div>
  );
};

interface ShortcutSectionProps {
  title: string;
  icon: React.ReactNode;
  shortcuts: Array<{ shortcut: string; description: string; type?: 'primary' | 'secondary' | 'vim' }>;
}

const ShortcutSection: React.FC<ShortcutSectionProps> = ({ title, icon, shortcuts }) => {
  return (
    <div className="mb-6">
      <div className="flex items-center space-x-2 mb-3">
        <div className="text-[var(--color-text-secondary)]">
          {icon}
        </div>
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)] uppercase tracking-wider">
          {title}
        </h3>
      </div>
      <div className="space-y-1">
        {shortcuts.map((item, index) => (
          <ShortcutItem
            key={index}
            shortcut={item.shortcut}
            description={item.description}
            type={item.type}
          />
        ))}
      </div>
    </div>
  );
};

export const KeyboardShortcutsHelp: React.FC<KeyboardShortcutsHelpProps> = ({
  isOpen,
  onClose,
}) => {
  const { vimSequence } = useEnhancedKeyboardShortcuts();

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const cmdKey = isMac ? '⌘' : 'Ctrl';

  const searchShortcuts = [
    { shortcut: `${cmdKey}+K`, description: 'Open command palette', type: 'primary' as const },
    { shortcut: 'Esc', description: 'Close command palette', type: 'secondary' as const },
  ];

  const navigationShortcuts = [
    { shortcut: `${cmdKey}+1`, description: 'Go to Dashboard', type: 'primary' as const },
    { shortcut: `${cmdKey}+2`, description: 'Go to Workspaces', type: 'primary' as const },
    { shortcut: `${cmdKey}+3`, description: 'Go to Backup Monitoring', type: 'primary' as const },
    { shortcut: `${cmdKey}+4`, description: 'Go to Alerts', type: 'primary' as const },
  ];

  const vimShortcuts = [
    { shortcut: 'g+d', description: 'Go to Dashboard', type: 'vim' as const },
    { shortcut: 'g+w', description: 'Go to Workspaces', type: 'vim' as const },
    { shortcut: 'g+c', description: 'Go to Capacities', type: 'vim' as const },
    { shortcut: 'g+b', description: 'Go to Backups', type: 'vim' as const },
    { shortcut: 'g+a', description: 'Go to Alerts', type: 'vim' as const },
    { shortcut: 'g+s', description: 'Go to Settings', type: 'vim' as const },
    { shortcut: 'g+p', description: 'Go to Policies', type: 'vim' as const },
  ];

  const helpShortcuts = [
    { shortcut: 'Shift+?', description: 'Show/hide keyboard shortcuts', type: 'secondary' as const },
    { shortcut: 'Esc', description: 'Close any modal or overlay', type: 'secondary' as const },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={
        <span className="flex items-center gap-3">
          <Keyboard className="h-6 w-6 text-[var(--color-text-secondary)]" />
          Keyboard Shortcuts
        </span>
      }
      footer={
        <div className="flex w-full items-center justify-between">
          <div className="text-sm text-[var(--color-text-secondary)]">
            Press <kbd className="px-2 py-1 bg-[var(--color-bg-subtle)] rounded text-xs font-mono">Esc</kbd> to close
          </div>
          <Button onClick={onClose}>Got it!</Button>
        </div>
      }
    >
      <p className="text-sm text-[var(--color-text-secondary)] mb-4">
        Use these shortcuts to navigate quickly through the application
      </p>

      {/* Vim Sequence Indicator */}
      {vimSequence && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md px-4 py-3 mb-4">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-green-800 dark:text-green-200">
              Vim sequence active: <kbd className="px-2 py-1 bg-green-200 dark:bg-green-800 rounded text-xs font-mono">{vimSequence}</kbd>
              <span className="ml-2 text-green-600 dark:text-green-300">Press the next key to navigate</span>
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left Column */}
            <div>
              <ShortcutSection
                title="Search & Command Palette"
                icon={<Search className="h-4 w-4" />}
                shortcuts={searchShortcuts}
              />

              <ShortcutSection
                title="Quick Navigation"
                icon={<Navigation className="h-4 w-4" />}
                shortcuts={navigationShortcuts}
              />
            </div>

            {/* Right Column */}
            <div>
              <ShortcutSection
                title="Vim-Style Navigation"
                icon={<Keyboard className="h-4 w-4" />}
                shortcuts={vimShortcuts}
              />

              <ShortcutSection
                title="Help & General"
                icon={<HelpCircle className="h-4 w-4" />}
                shortcuts={helpShortcuts}
              />
            </div>
          </div>

          {/* Tips Section */}
          <div className="mt-8 pt-6 border-t border-[var(--color-border)]">
            <h3 className="text-sm font-semibold text-[var(--color-text-primary)] uppercase tracking-wider mb-3">
              Tips
            </h3>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <div className="flex">
                <HelpCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200">
                    Getting the Most Out of Keyboard Shortcuts
                  </h4>
                  <div className="mt-2 text-sm text-blue-700 dark:text-blue-300 space-y-1">
                    <p>• Use <kbd className="px-1 py-0.5 bg-blue-200 dark:bg-blue-800 rounded text-xs font-mono">Cmd+K</kbd> or <kbd className="px-1 py-0.5 bg-blue-200 dark:bg-blue-800 rounded text-xs font-mono">Ctrl+K</kbd> to quickly search and navigate</p>
                    <p>• Vim-style shortcuts let you navigate with two keypresses (press <kbd className="px-1 py-0.5 bg-blue-200 dark:bg-blue-800 rounded text-xs font-mono">g</kbd> then the destination key)</p>
                    <p>• Number shortcuts (<kbd className="px-1 py-0.5 bg-blue-200 dark:bg-blue-800 rounded text-xs font-mono">Cmd+1-4</kbd>) provide quick access to main sections</p>
                    <p>• Most keyboard shortcuts work even when typing in search fields</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        {/* Browser Compatibility Note */}
        <div className="mt-4 text-xs text-[var(--color-text-secondary)] text-center">
          <p>
            Shortcuts are optimized for modern browsers.
            {isMac ? ' ⌘ (Cmd) key is used on macOS.' : ' Ctrl key is used on Windows/Linux.'}
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default KeyboardShortcutsHelp;
